import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { CheckCircle, Stethoscope, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Logo1ClinIcon } from '../components/Logo1Clin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function KioskPage() {
  const { token } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [patient, setPatient] = useState(null);
  const [checkedIn, setCheckedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    chief_complaint: '', bp_systolic: '', bp_diastolic: '', pulse: '',
    temperature: '', weight: '', spo2: '', pain_scale: 3, additional_notes: ''
  });

  useEffect(() => {
    if (token) {
      axios.get(`${BACKEND_URL}/api/kiosk/appointment/${token}`)
        .then(res => {
          if (res.data.checked_in) { setCheckedIn(true); }
          setAppointment(res.data.appointment);
          setPatient(res.data.patient);
        })
        .catch(err => setError(err.response?.data?.detail || 'Invalid or expired check-in link'))
        .finally(() => setLoading(false));
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.chief_complaint.trim()) { toast.error('Please describe your main complaint'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/kiosk/checkin`, {
        token,
        ...form,
        bp_systolic: parseInt(form.bp_systolic) || 0,
        bp_diastolic: parseInt(form.bp_diastolic) || 0,
        pulse: parseInt(form.pulse) || 0,
        temperature: parseFloat(form.temperature) || 0,
        weight: parseFloat(form.weight) || 0,
        spo2: parseInt(form.spo2) || 0,
        pain_scale: form.pain_scale,
      });
      setCheckedIn(true);
      toast.success('Check-in successful!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 border-3 border-teal-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="max-w-md w-full text-center">
        <CardContent className="p-8">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily:'Manrope'}}>Invalid Link</h2>
          <p className="text-slate-500">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (checkedIn) return (
    <div className="min-h-screen bg-emerald-50 flex items-center justify-center p-6">
      <Card className="max-w-md w-full text-center border-emerald-200">
        <CardContent className="p-10">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-emerald-800 mb-2" style={{fontFamily:'Manrope'}}>Check-in Complete!</h2>
          <p className="text-emerald-600 mb-4">Thank you. The doctor will see you shortly.</p>
          {appointment && (
            <Badge className="bg-emerald-100 text-emerald-800 text-lg px-4 py-2">
              Token #{appointment.token_number}
            </Badge>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="kiosk-page">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-3">
            <Logo1ClinIcon size={56} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily:'Manrope'}}>Patient Check-In</h1>
          <p className="text-sm text-slate-500 mt-1">1Clin - Self Check-In Kiosk</p>
        </div>

        {/* Patient Info */}
        {patient && (
          <Card className="border-teal-200 bg-teal-50/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-teal-700" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{patient.name}</p>
                  <p className="text-sm text-slate-500">{patient.age ? `${patient.age}y` : ''} {patient.gender || ''} | Token #{appointment?.token_number}</p>
                </div>
              </div>
              {patient.allergies?.length > 0 && (
                <div className="mt-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-amber-700">Allergies: {patient.allergies.join(', ')}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Check-in Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>What brings you in today?</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                data-testid="kiosk-complaint"
                value={form.chief_complaint}
                onChange={e => setForm({...form, chief_complaint: e.target.value})}
                placeholder="Describe your main symptoms or reason for visit..."
                className="min-h-[100px] text-base"
                required
              />
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Vitals (if you have a BP monitor / thermometer)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">BP Systolic</Label>
                  <Input data-testid="kiosk-bp-sys" type="number" value={form.bp_systolic} onChange={e => setForm({...form, bp_systolic: e.target.value})} className="h-12 mt-1 text-lg" placeholder="120" />
                </div>
                <div>
                  <Label className="text-xs">BP Diastolic</Label>
                  <Input data-testid="kiosk-bp-dia" type="number" value={form.bp_diastolic} onChange={e => setForm({...form, bp_diastolic: e.target.value})} className="h-12 mt-1 text-lg" placeholder="80" />
                </div>
                <div>
                  <Label className="text-xs">Pulse (bpm)</Label>
                  <Input type="number" value={form.pulse} onChange={e => setForm({...form, pulse: e.target.value})} className="h-12 mt-1 text-lg" placeholder="72" />
                </div>
                <div>
                  <Label className="text-xs">Weight (kg)</Label>
                  <Input type="number" value={form.weight} onChange={e => setForm({...form, weight: e.target.value})} className="h-12 mt-1 text-lg" placeholder="70" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Pain Level</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-500">No Pain</span>
                <span className="text-lg font-bold text-teal-700">{form.pain_scale}/10</span>
                <span className="text-sm text-slate-500">Severe</span>
              </div>
              <Slider
                data-testid="kiosk-pain-scale"
                value={[form.pain_scale]}
                onValueChange={([v]) => setForm({...form, pain_scale: v})}
                max={10}
                step={1}
                className="w-full"
              />
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <Label>Additional Notes (optional)</Label>
              <Textarea value={form.additional_notes} onChange={e => setForm({...form, additional_notes: e.target.value})} placeholder="Any other information..." className="mt-1.5" />
            </CardContent>
          </Card>

          <Button type="submit" className="w-full h-14 text-lg bg-teal-700 hover:bg-teal-800 text-white rounded-xl" disabled={submitting} data-testid="kiosk-submit-btn">
            {submitting ? 'Checking in...' : 'Complete Check-In'}
          </Button>
        </form>
      </div>
    </div>
  );
}
